import axios from 'axios'
import { useAuthStore } from '../store/authStore'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important: send/receive cookies
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors - just clear state, let React Router handle redirect
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only logout if we were previously authenticated (avoid loop on fresh load)
      const { isAuthenticated, logout } = useAuthStore.getState()
      if (isAuthenticated) {
        logout()
      }
    }
    return Promise.reject(error)
  }
)

// Wallets API
export const walletsApi = {
  getAll: (params?: any) => api.get('/wallets', { params }),
  getById: (id: string) => api.get(`/wallets/${id}`),
  create: (data: { address: string; label?: string }) => api.post('/wallets', data),
  delete: (id: string) => api.delete(`/wallets/${id}`),
  analyze: (id: string) => api.post(`/wallets/${id}/analyze`),
  track: (id: string) => api.post(`/wallets/${id}/track`),
  untrack: (id: string) => api.delete(`/wallets/${id}/track`),
  getTop: (category: string) => api.get(`/wallets/top/${category}`),
}

// Markets API
export const marketsApi = {
  getAll: (params?: any) => api.get('/markets', { params }),
  getById: (id: string) => api.get(`/markets/${id}`),
  getOpportunities: (id: string) => api.get(`/markets/${id}/opportunities`),
  getWhales: (id: string) => api.get(`/markets/${id}/whales`),
  calculateEV: (id: string, data: any) => api.post(`/markets/${id}/calculate-ev`, data),
  getCategories: () => api.get('/markets/categories/all'),
  track: (id: string) => api.post(`/markets/${id}/track`),
  untrack: (id: string) => api.delete(`/markets/${id}/track`),
}

// Bots API
export const botsApi = {
  getAll: () => api.get('/bots'),
  getById: (id: string) => api.get(`/bots/${id}`),
  create: (data: any) => api.post('/bots', data),
  update: (id: string, data: any) => api.patch(`/bots/${id}`, data),
  delete: (id: string) => api.delete(`/bots/${id}`),
  getExecutions: (id: string, params?: any) => api.get(`/bots/${id}/executions`, { params }),
  toggle: (id: string) => api.post(`/bots/${id}/toggle`),
  getAnalytics: (id: string, params?: { days?: number }) => api.get(`/bots/${id}/analytics`, { params }),
}

// Analytics API
export const analyticsApi = {
  getOverview: () => api.get('/analytics/overview'),
  getPortfolio: () => api.get('/analytics/portfolio'),
  getWhales: () => api.get('/analytics/whales'),
  scan: () => api.post('/analytics/scan'),
  getStats: () => api.get('/analytics/stats'),
  getPerformance: () => api.get('/analytics/performance'),
}

// Auth API
export const authApi = {
  login: (email: string, password: string) => 
    api.post('/auth/login', { email, password }),
  register: (email: string, password: string, name?: string) => 
    api.post('/auth/register', { email, password, name }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    api.patch('/auth/password', data),
}

// Trades API
export const tradesApi = {
  getAll: (params?: any) => api.get('/trades', { params }),
  getById: (id: string) => api.get(`/trades/${id}`),
  getStats: (params?: any) => api.get('/trades/stats/overview', { params }),
  execute: (data: { marketId: string; side: 'YES' | 'NO'; amount: number; maxPrice: number }) =>
    api.post('/trades/execute', data),
}

// Alerts API
export const alertsApi = {
  getAll: (params?: any) => api.get('/alerts', { params }),
  markRead: (id: string) => api.patch(`/alerts/${id}/read`),
  markAllRead: () => api.post('/alerts/read-all'),
  delete: (id: string) => api.delete(`/alerts/${id}`),
}

// Opportunities API
export const opportunitiesApi = {
  getAll: (params?: any) => api.get('/opportunities', { params }),
  getById: (id: string) => api.get(`/opportunities/${id}`),
}

// Advanced Orders API
export const advancedOrdersApi = {
  getAll: (params?: { status?: string; marketId?: string }) => api.get('/advanced-orders', { params }),
  getById: (id: string) => api.get(`/advanced-orders/${id}`),
  create: (data: {
    marketId: string
    side: 'YES' | 'NO'
    size: number
    type: 'STOP_LOSS' | 'TAKE_PROFIT' | 'TRAILING_STOP' | 'BRACKET' | 'OCO'
    entryPrice?: number
    stopLossPrice?: number
    stopLossPercent?: number
    takeProfitPrice?: number
    takeProfitPercent?: number
    trailingDistance?: number
    trailingPercent?: boolean
    isBracket?: boolean
    expiresAt?: string
  }) => api.post('/advanced-orders', data),
  cancel: (id: string) => api.delete(`/advanced-orders/${id}`),
  updateTrailing: (id: string, trailingDistance: number) => 
    api.patch(`/advanced-orders/${id}/trailing`, { trailingDistance }),
  getSummary: () => api.get('/advanced-orders/summary/active'),
}

// Strategies API
export const strategiesApi = {
  getAll: () => api.get('/strategies'),
  getById: (id: string) => api.get(`/strategies/${id}`),
  create: (data: {
    name: string
    description?: string
    rules: Array<{
      condition: string
      value: string
      action: string
      positionSize?: number
    }>
    isActive?: boolean
  }) => api.post('/strategies', data),
  update: (id: string, data: any) => api.patch(`/strategies/${id}`, data),
  delete: (id: string) => api.delete(`/strategies/${id}`),
  toggle: (id: string) => api.post(`/strategies/${id}/toggle`),
  getTemplates: () => api.get('/strategies/templates/list'),
}

// Backtests API
export const backtestsApi = {
  getAll: () => api.get('/backtests'),
  getById: (id: string) => api.get(`/backtests/${id}`),
  run: (data: {
    strategyId: string
    startDate: string
    endDate: string
    initialCapital?: number
    markets?: string[]
  }) => api.post('/backtests', data),
  delete: (id: string) => api.delete(`/backtests/${id}`),
  compare: (backtestIds: string[]) => api.post('/backtests/compare', { backtestIds }),
  getStats: () => api.get('/backtests/stats/summary'),
}

// AI Chat API
export const aiChatApi = {
  chat: (message: string, context?: { history?: Array<{ role: string; content: string }> }) =>
    api.post('/ai-chat', { message, context }),
  getSuggestions: () => api.get('/ai-chat/suggestions'),
  query: (type: string, filters?: any) => api.post('/ai-chat/query', { type, filters }),
}

// User API
export const userApi = {
  updateProfile: (data: { name: string; email: string }) => api.patch('/users/profile', data),
  updateNotifications: (data: any) => api.patch('/users/notifications', data),
  deleteAccount: () => api.delete('/users/account'),
}

// Polymarket CLOB API
export const polymarketApi = {
  // Get order data for client-side signing (SECURE)
  generateOrderData: (data: { marketId: string; side: 'YES' | 'NO'; size: number; maxPrice: number }) =>
    api.post('/polymarket/order-data', data),
  
  // Submit signed order (client signs, we submit)
  submitSignedOrder: (data: { 
    signedOrder: any;
    marketId: string; 
    side: 'YES' | 'NO'; 
    size: number; 
    price: number;
  }) => api.post('/polymarket/submit-signed-order', data),
  
  // Order book
  getOrderBook: (tokenId: string) => api.get(`/polymarket/orderbook/${tokenId}`),
  getPrice: (tokenId: string, side: 'BUY' | 'SELL') => 
    api.get(`/polymarket/price/${tokenId}`, { params: { side } }),
  
  // Orders
  cancelOrder: (orderId: string) => api.delete(`/polymarket/order/${orderId}`),
  cancelAllOrders: () => api.delete('/polymarket/orders'),
  getOpenOrders: () => api.get('/polymarket/orders'),
  getOrder: (orderId: string) => api.get(`/polymarket/order/${orderId}`),
  
  // Trades
  getTrades: (params?: { market?: string; after?: number; before?: number }) =>
    api.get('/polymarket/trades', { params }),
  
  // Account
  getBalance: () => api.get('/polymarket/balance'),
  
  // Markets
  getMarkets: () => api.get('/polymarket/markets'),
  getMarket: (conditionId: string) => api.get(`/polymarket/market/${conditionId}`),
}

export default api
